import { NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { DEFAULT_MAP_LATITUDE, DEFAULT_MAP_LONGITUDE, ILatLon } from '@curbnturf/entities';
import { LoadingController } from '@ionic/angular';
import { OpenStreetMapComponent } from '../open-street-map/open-street-map.component';

const DEFAULT_LAT = 44.094533;
const DEFAULT_LON = -116.74177;

@Component({
  standalone: true,
  selector: 'curbnturf-coordinates-map',
  templateUrl: './coordinates-map.component.html',
  styleUrls: ['./coordinates-map.component.scss'],
  imports: [NgIf, forwardRef(() => OpenStreetMapComponent)],
})
export class CoordinatesMapComponent implements OnInit {
  @Output() locationClicked = new EventEmitter<ILatLon>();

  @Output() close = new EventEmitter<void>();

  @ViewChild('openStreetMap') openStreetMapComponent: OpenStreetMapComponent;
  @ViewChild('mapContainer') mapContainerEl: ElementRef;

  location?: ILatLon;
  coordinates: ILatLon;

  @Input() pins: ILatLon[] = [];
  @Input() center: ILatLon;
  @Input() text: string = 'Verify the location for the guest';
  @Input() draggable: boolean = true;

  zoom = 18;

  get isNative() {
    return Capacitor.isNativePlatform();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private loadingController: LoadingController,
    private alertFacade: AlertFacade,
  ) {}

  async ngOnInit() {
    const existingPin = this.pins?.find((pin) => !!(pin.lat && pin.lon));
    if (this.center && this.center.lat && this.center.lon) {
      this.location = this.center;
      this.cdr.detectChanges();
    } else if (existingPin) {
      this.location = existingPin;
      setTimeout(() => {
        this.openStreetMapComponent.addSelectedLocationMarker({lat: existingPin.lat, lon: existingPin.lon});
        setTimeout(() => {
          this.openStreetMapComponent.setMapZoom(this.zoom);
        }, 500);
      }, 100)
      this.cdr.detectChanges();
    } else {
      const loading = await this.loadingController.create({
        duration: 15000,
        message: 'Finding your current location...',
      });
      await loading.present();
      Geolocation.getCurrentPosition()
        .then(async (location) => {
          await loading.dismiss();
          this.location = {
            lat: location.coords.latitude,
            lon: location.coords.longitude,
          };
          setTimeout(() => {
            this.openStreetMapComponent.addSelectedLocationMarker({lat: location.coords.latitude, lon: location.coords.longitude});
            setTimeout(() => {
              this.openStreetMapComponent.setMapZoom(this.zoom);
            }, 500);
          }, 100);
          this.cdr.detectChanges();
        })
        .catch(async () => {
          await loading.dismiss();
          this.alertFacade.display({
            level: 'error',
            title: 'Location Not Found',
            body: 'Unable to find your location.',
          });
          this.location = {
            lat: DEFAULT_LAT,
            lon: DEFAULT_LON,
          };
          setTimeout(() => {
            this.openStreetMapComponent.setMapZoom(this.zoom);
          }, 500);
        });

      return;
    }
  }

  showMap(): boolean {
    return Boolean(this.mapContainerEl?.nativeElement?.offsetParent);
  }

  newPosition(coordinates: ILatLon) {
    this.coordinates = coordinates;
    this.openStreetMapComponent.addSelectedLocationMarker(coordinates);
    this.locationClicked.emit(coordinates);
  }

  updateCoordinates(coordinates: ILatLon) {
    this.coordinates = coordinates;
  }

  protected readonly DEFAULT_LAT = DEFAULT_LAT;
  protected readonly DEFAULT_MAP_LATITUDE = DEFAULT_MAP_LATITUDE;
  protected readonly DEFAULT_MAP_LONGITUDE = DEFAULT_MAP_LONGITUDE;
}
